// src/components/MaturaExamRunner/MaturaExamCore.js

import { quizTypeRegistry } from "../QuizRunner/quizTypeRegistry";
import { quizTypeRegistryClosed } from "../QuizRunner/quizTypeRegistryClosed";
import { quizTypeRegistryOpen } from "../QuizRunner/quizTypeRegistryOpen";
import { createCombinatoricsExamRegistry } from "../../matura/combinatoricsExamRegistry";

// ─────────────────────────────────────────────────────────────
// 1. Wspólny rejestr adapterów
// ─────────────────────────────────────────────────────────────

export const combinedRegistry = createCombinatoricsExamRegistry({
  ...(quizTypeRegistry || {}),
  ...(quizTypeRegistryClosed || {}),
  ...(quizTypeRegistryOpen || {}),
});

// ─────────────────────────────────────────────────────────────
// 2. Numer zadania: "12", "12.1", "12.2" → sortowanie
// ─────────────────────────────────────────────────────────────

export function parseTaskNo(taskNo) {
  const s = String(taskNo || "").trim();
  const m = s.match(/^(\d+)(?:\.(\d+))?$/);
  if (!m) return { base: 999, part: 0 };
  return {
    base: Number(m[1]),
    part: m[2] ? Number(m[2]) : 0,
  };
}

export function compareTaskNo(aNo, bNo) {
  const a = parseTaskNo(aNo);
  const b = parseTaskNo(bNo);
  if (a.base !== b.base) return a.base - b.base;
  return a.part - b.part;
}

// ─────────────────────────────────────────────────────────────
// 3. Deterministyczny RNG (seed) – bez ruszania adapterów
// ─────────────────────────────────────────────────────────────

// String → 32-bit seed (xmur3)
function xmur3(str) {
  let h = 1779033703 ^ str.length;
  for (let i = 0; i < str.length; i++) {
    h = Math.imul(h ^ str.charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  return function () {
    h = Math.imul(h ^ (h >>> 16), 2246822507);
    h = Math.imul(h ^ (h >>> 13), 3266489909);
    h ^= h >>> 16;
    return h >>> 0;
  };
}

// 32-bit seed → RNG [0,1) (mulberry32)
function mulberry32(a) {
  return function () {
    let t = (a += 0x6D2B79F5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// Uruchamia funkcję z tymczasowo podmienionym Math.random (sync)
function withSeededRandom(seedStr, fn) {
  const seedFn = xmur3(String(seedStr || ""));
  const seed = seedFn(); // uint32
  const rng = mulberry32(seed);

  const origRandom = Math.random;
  Math.random = rng;
  try {
    return fn();
  } finally {
    Math.random = origRandom;
  }
}

// Stabilny “klucz” zadania (żeby seed był per zadanie)
function makeStableTaskKey(meta, idx) {
  const taskNo = meta?.taskNo ?? meta?.number ?? idx + 1;
  const slotNo = meta?.slotNo ?? "";
  const code3 = meta?.code3 ?? "";
  const quizTypeKey = meta?.quizTypeKey ?? meta?.type ?? "";
  return `taskNo:${taskNo}|slotNo:${slotNo}|code3:${code3}|type:${quizTypeKey}`;
}

function normalizeExamTaskMeta(meta, idx) {
  if (!meta || typeof meta !== "object") return meta;

  const taskNo =
    meta.taskNo ??
    meta.blueprintTaskNo ??
    meta.number ??
    meta.slotNo ??
    meta.source?.taskNumber ??
    idx + 1;
  const slotNo = meta.slotNo ?? meta.number ?? taskNo;

  return {
    ...meta,
    taskNo: String(taskNo),
    number: String(meta.number ?? taskNo),
    slotNo,
  };
}

// ─────────────────────────────────────────────────────────────
// 4. Generowanie examTasks z meta + adapterów
//    + opcjonalny seedBase (np. attemptId) dla deterministyki
// ─────────────────────────────────────────────────────────────
export function buildExamTasks(
  tasksMeta,
  registry = combinedRegistry,
  savedAnswers = null,
  seedBase = null
) {
  if (!Array.isArray(tasksMeta) || tasksMeta.length === 0) {
    return {
      examTasks: [],
      initialAnswers: [],
      initialShowExplanation: [],
    };
  }

  // ✅ bezpiecznie: tasksMeta może zawierać null/undefined
  const normalizedMeta = tasksMeta.map((meta, idx) => normalizeExamTaskMeta(meta, idx));
  const sorted = [...normalizedMeta].sort((a, b) => compareTaskNo(a?.taskNo, b?.taskNo));

  const generated = [];

  sorted.forEach((meta, idx) => {
    // ✅ krytyczny guard – usuwa crash "reading 'type'"
    if (!meta) {
      console.warn("[MaturaExamCore] Pusty meta w tasksMeta (idx)", idx);
      return;
    }

    const key = meta.quizTypeKey || meta.type;
    if (!key) {
      console.warn("[MaturaExamCore] Meta bez quizTypeKey/type:", meta);
      return;
    }

    const adapter = registry[key];

    if (!adapter || typeof adapter.generateTask !== "function") {
      console.warn(
        "[MaturaExamCore] Brak adaptera lub generateTask dla typu",
        key,
        "meta:",
        meta
      );
      return;
    }

    let task;
    try {
      // 🔥 Najważniejsze: deterministyczna generacja przy kontynuacji
      // seedBase powinien być attemptId (przekażemy z MaturaExamRunner/MaturaExamPage)
      if (seedBase) {
        const seedStr = `${seedBase}::${makeStableTaskKey(meta, idx)}`;
        task = withSeededRandom(seedStr, () => adapter.generateTask(meta.combinatoricsSource ? { ...meta, seed: seedStr } : meta));
      } else {
        // Zachowanie wstecz (losowo), jeśli seedBase nie podany
        task = adapter.generateTask(meta);
      }
    } catch (e) {
      console.warn(
        "[MaturaExamCore] generateTask wyjątek dla typu",
        key,
        e,
        "meta:",
        meta
      );
      return;
    }

    generated.push({
      index: generated.length,
      meta,
      typeKey: key,
      task,
    });
  });

  // (opcjonalnie) debug
  console.log(
    "[MaturaExamCore] buildExamTasks:",
    "tasksMeta=",
    tasksMeta?.length,
    "sorted=",
    sorted?.length,
    "generated=",
    generated.length,
    "seeded=",
    !!seedBase,
    "sampleMeta=",
    sorted?.[0]
  );

  // Answers: zachowaj tyle ile się da, resztę uzupełnij
  let initialAnswers;
  if (Array.isArray(savedAnswers) && savedAnswers.length > 0) {
    initialAnswers = generated.map((_, i) =>
      i < savedAnswers.length ? savedAnswers[i] : ""
    );
  } else {
    initialAnswers = generated.map(() => "");
  }

  const initialShowExplanation = generated.map(() => false);

  return {
    examTasks: generated,
    initialAnswers,
    initialShowExplanation,
  };
}

// ─────────────────────────────────────────────────────────────
// 5. Pagina: dzielenie examTasks na strony (max 6 pkt / stronę)
// ─────────────────────────────────────────────────────────────
export function paginateExamTasks(examTasks, options = {}) {
  const { savedPage = null, previousPageDone = null } = options;

  if (!Array.isArray(examTasks) || examTasks.length === 0) {
    return {
      pages: [],
      pageDone: [],
      initialPage: 0,
    };
  }

  const pages = [];
  let current = { tasks: [], totalPoints: 0 };

  for (const et of examTasks) {
    // ✅ dodatkowy guard – na wypadek, gdyby examTasks miało dziury
    if (!et || !et.meta) continue;

    const pts =
      typeof et.meta?.maxPoints === "number" && et.meta.maxPoints > 0
        ? et.meta.maxPoints
        : 1;

    if (current.tasks.length > 0 && current.totalPoints + pts > 6) {
      pages.push(current);
      current = { tasks: [], totalPoints: 0 };
    }

    // ✅ ważne: SPREAD
    current.tasks.push({ ...et, points: pts });
    current.totalPoints += pts;
  }

  if (current.tasks.length > 0) pages.push(current);

  let pageDone;
  if (Array.isArray(previousPageDone) && previousPageDone.length === pages.length) {
    pageDone = previousPageDone;
  } else {
    pageDone = pages.map(() => false);
  }

  let initialPage = 0;
  if (typeof savedPage === "number" && savedPage >= 0 && savedPage < pages.length) {
    initialPage = savedPage;
  }

  return {
    pages,
    pageDone,
    initialPage,
  };
}

// ─────────────────────────────────────────────────────────────
// 6. (Opcjonalnie) Odczyt meta/mode z sessionStorage
//    - przydaje się w miejscu, gdzie tworzysz <MaturaExamRunner />
// ─────────────────────────────────────────────────────────────

const META_CACHE_KEY = (examTaskId) => `matura:lastTasksMeta:${examTaskId}`;

export function getMaturaMetaFromSession(examTaskId) {
  try {
    if (typeof window === "undefined" || !window.sessionStorage) return null;
    const raw = window.sessionStorage.getItem(META_CACHE_KEY(examTaskId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

export function getMaturaModeFromSession(examTaskId) {
  const meta = getMaturaMetaFromSession(examTaskId);
  return meta?.mode || null;
}
